import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { reportService } from '../../services/reportService';
import { fleetService } from '../../services/fleetService';
import DailyReportTable from '../../components/reports/DailyReportTable';
import RevenueReportChart from '../../components/reports/RevenueReportChart';
import ReportExportOptions from '../../components/reports/ReportExportOptions';
import { Calendar, FilterX, TrendingUp, ArrowDown, ArrowUp } from 'lucide-react';

const DailyReportsPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [reportData, setReportData] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [fleets, setFleets] = useState([]);
    const [summary, setSummary] = useState({
        total_orders: 0,
        completed_orders: 0,
        total_revenue: 0,
        average_fare: 0,
        order_growth: 0,
        revenue_growth: 0
    });
    const [activeTab, setActiveTab] = useState('table');
    const [filters, setFilters] = useState({
        fleet_id: user.user_type === 'admin' ? '' : user.fleet_id,
        start_date: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        period: 'daily'
    });

    const isAdmin = user.user_type === 'admin';

    // 時間週期選項
    const periodOptions = [
        { value: 'daily', label: '日報表' },
        { value: 'weekly', label: '週報表' },
        { value: 'monthly', label: '月報表' }
    ];

    useEffect(() => {
        fetchDailyReport();
        if (isAdmin) {
            fetchFleets();
        }
    }, [filters.fleet_id, filters.start_date, filters.end_date, filters.period]);

    const fetchDailyReport = async () => {
        setIsLoading(true);
        try {
            // 獲取報表數據
            const params = {
                fleet_id: filters.fleet_id || undefined,
                start_date: filters.start_date,
                end_date: filters.end_date,
                period: filters.period
            };

            const { data, chart_data, summary: reportSummary } = await reportService.getDailyReport(params);
            setReportData(data);
            setChartData(chart_data);
            setSummary(reportSummary);
        } catch (error) {
            console.error('Error fetching daily report:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchFleets = async () => {
        try {
            const fleetsData = await fleetService.getFleets();
            setFleets(fleetsData);
        } catch (error) {
            console.error('Error fetching fleets:', error);
        }
    };

    const handleFleetChange = (e) => {
        setFilters(prev => ({
            ...prev,
            fleet_id: e.target.value
        }));
    };

    const handleDateChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handlePeriodChange = (e) => {
        setFilters(prev => ({
            ...prev,
            period: e.target.value
        }));
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const handleResetFilters = () => {
        setFilters({
            fleet_id: user.user_type === 'admin' ? '' : user.fleet_id,
            start_date: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
            end_date: new Date().toISOString().split('T')[0],
            period: 'daily'
        });
    };

    const handleExportReport = (format) => {
        try {
            reportService.exportDailyReport({
                fleet_id: filters.fleet_id || undefined,
                start_date: filters.start_date,
                end_date: filters.end_date,
                period: filters.period,
                format
            });
        } catch (error) {
            console.error('Error exporting report:', error);
        }
    };

    // 格式化數字顯示千分位
    const formatNumber = (number) => {
        return number?.toLocaleString() || '0';
    };

    // 格式化金額
    const formatCurrency = (amount) => {
        return `$${amount?.toLocaleString() || '0'}`;
    };

    // 渲染增長指標
    const renderGrowth = (value, isReverse = false) => {
        if (value === undefined || value === null) return null;

        let isPositive = value > 0;
        if (isReverse) isPositive = !isPositive;

        const Icon = isPositive ? ArrowUp : ArrowDown;
        const colorClass = isPositive ? 'text-green-500' : 'text-red-500';

        return (
            <div className={`flex items-center ${colorClass}`}>
                <Icon className="w-5 h-5 mr-1" />
                <span className="text-sm">{Math.abs(value)}%</span>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">營運報表</h1>
                <ReportExportOptions onExport={handleExportReport} />
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center mb-6 text-gray-500">
                    <Calendar className="w-5 h-5 mr-2" />
                    <span>報表期間: {filters.start_date} 至 {filters.end_date}</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {isAdmin && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                選擇車隊
                            </label>
                            <select
                                value={filters.fleet_id}
                                onChange={handleFleetChange}
                                className="w-full border border-gray-300 rounded-md p-2"
                            >
                                <option value="">所有車隊</option>
                                {fleets.map(fleet => (
                                    <option key={fleet.fleet_id} value={fleet.fleet_id}>
                                        {fleet.fleet_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            開始日期
                        </label>
                        <input
                            type="date"
                            value={filters.start_date}
                            onChange={(e) => handleDateChange('start_date', e.target.value)}
                            className="w-full border border-gray-300 rounded-md p-2"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            結束日期
                        </label>
                        <input
                            type="date"
                            value={filters.end_date}
                            onChange={(e) => handleDateChange('end_date', e.target.value)}
                            className="w-full border border-gray-300 rounded-md p-2"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            報表類型
                        </label>
                        <select
                            value={filters.period}
                            onChange={handlePeriodChange}
                            className="w-full border border-gray-300 rounded-md p-2"
                        >
                            {periodOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="mt-4 flex justify-end">
                    <button
                        onClick={handleResetFilters}
                        className="flex items-center text-sm text-blue-500 hover:text-blue-700"
                    >
                        <FilterX className="w-4 h-4 mr-1" />
                        重置篩選
                    </button>
                </div>
            </div>

            {/* 統計摘要卡片 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="bg-white rounded-lg shadow-sm p-4">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm text-gray-500">總訂單數</h3>
                        <TrendingUp className="w-5 h-5 text-blue-500" />
                    </div>
                    <div className="flex justify-between items-end">
                        <p className="text-2xl font-bold">{formatNumber(summary.total_orders)}</p>
                        {renderGrowth(summary.order_growth)}
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-4">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm text-gray-500">完成率</h3>
                        <TrendingUp className="w-5 h-5 text-green-500" />
                    </div>
                    <div className="flex justify-between items-end">
                        <p className="text-2xl font-bold">
                            {Math.round((summary.completed_orders / summary.total_orders) * 100) || 0}%
                        </p>
                        {renderGrowth(summary.completion_rate_growth)}
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-4">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm text-gray-500">總收入</h3>
                        <TrendingUp className="w-5 h-5 text-purple-500" />
                    </div>
                    <div className="flex justify-between items-end">
                        <p className="text-2xl font-bold">{formatCurrency(summary.total_revenue)}</p>
                        {renderGrowth(summary.revenue_growth)}
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-4">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm text-gray-500">平均車資</h3>
                        <TrendingUp className="w-5 h-5 text-yellow-500" />
                    </div>
                    <div className="flex justify-between items-end">
                        <p className="text-2xl font-bold">{formatCurrency(summary.average_fare)}</p>
                        {renderGrowth(summary.average_fare_growth)}
                    </div>
                </div>
            </div>

            {/* 報表標籤切換 */}
            <div className="flex border-b border-gray-200">
                <button
                    className={`px-6 py-3 border-b-2 text-sm font-medium ${activeTab === 'table'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    onClick={() => handleTabChange('table')}
                >
                    表格視圖
                </button>
                <button
                    className={`px-6 py-3 border-b-2 text-sm font-medium ${activeTab === 'chart'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    onClick={() => handleTabChange('chart')}
                >
                    圖表視圖
                </button>
            </div>

            {/* 報表內容 */}
            <div>
                {activeTab === 'table' ? (
                    <DailyReportTable
                        data={reportData}
                        loading={isLoading}
                        period={filters.period}
                    />
                ) : (
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <h3 className="font-bold mb-4">營運趨勢</h3>
                        <RevenueReportChart
                            data={chartData}
                            period={filters.period}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default DailyReportsPage;